import React from "react";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import JobDescription from "./JobDescription";
import AgencyBriefing from "./AgencyBriefing";
import benefitOptionsType from "./types/benefitOptionsType";
import vacancyReasonOptionsType from "./types/vacancyReasonOptionsType";
import {
  Badge,
  BadgeTooltip,
  JobProfileLeftWrapper,
  JobTitle,
  SectionHeader,
  SectionHeaderText,
  Section,
  SectionContent,
  MultiSection,
  MutiSectionItem,
  Tooltip,
  BenefitsListWrapper,
  BenefitsListItem,
  KeySkillsListWrapper,
  KeySkillsListItem,
} from "./styles/job-profile-left-styles";

const JobProfileLeft = ({
  agencyBriefing,
  employerAccountType,
  jobTitle,
  recruiterFeePercentage,
  salaryMin,
  salaryRange,
  salaryType,
  contractDuration,
  contractDurationInterval,
  keySkills,
  description,
  jobType,
  degreeRequired,
  visaSponsorship,
  considerRelocation,
  benefitIds,
  benefitOptions,
  vacancyReason,
  vacancyReasonOptions,
  isRecruiter,
  exclusive,
  finalSalary,
  positionFilled,
  publishedStatus,
  isPortalUser,
  currencySymbol,
}) => {
  const benefits = benefitOptions.filter(
    (option) => benefitIds.includes(option.id) && option.name
  );

  const vacancyReasonType = vacancyReasonOptions.filter(
    (option) => option.id === vacancyReason
  )[0].name;

  const listKeySkills = keySkills && keySkills.split(",");

  const percentageFeeTooltip = `
    The percentage fee displayed is the full amount<br>
    attached to this role. In the event of a successful<br>
    placement, you would invoice Hiring Hub for 50% of<br>
    the total fee payable
  `;

  const underReviewTooltipText = `
    Your job is under review, which means your<br>
    account manager needs to qualify the<br>
    role before it can go to our agencies.<br>
    To accelerate this process, please<br>
    contact your account manager.
  `;

  const exclusiveJobTooltip = `
    An exclusive job is defined by Hiring Hub that the<br>
    role has not been shared with any other external<br>
    recruitment partners. We appreciate that employers<br>
    will have their own in-house functions and careers pages.
  `;

  return (
    <JobProfileLeftWrapper>
      <Section>
        <SectionHeader>Job Title</SectionHeader>
        <SectionContent large>
          <JobTitle>{jobTitle}</JobTitle>
          {exclusive && (
            <Badge
              data-tip={isRecruiter && exclusiveJobTooltip}
              data-for="exclusive-job-tooltip"
            >
              Exclusive
            </Badge>
          )}
          {employerAccountType === "job_vendor" && isRecruiter && (
            <Badge
              data-test-id="split-fee-badge"
              data-tip={percentageFeeTooltip}
              data-for="split-fee-badge-tooltip"
            >
              Split Fee
            </Badge>
          )}
          {publishedStatus === "under_review" && !isRecruiter && (
            <Badge
              data-test-id="under-review-badge"
              data-tip={underReviewTooltipText}
              data-for="under-review-tooltip"
            >
              Under Review
            </Badge>
          )}
        </SectionContent>
        <BadgeTooltip>
          <ReactTooltip
            multiline
            place="bottom"
            type="dark"
            effect="solid"
            aria-haspopup="true"
            id="under-review-tooltip"
          />
          <ReactTooltip
            multiline
            place="bottom"
            type="dark"
            effect="solid"
            aria-haspopup="true"
            id="split-fee-badge-tooltip"
          />
          {isRecruiter && (
            <ReactTooltip
              multiline
              place="bottom"
              type="dark"
              effect="solid"
              aria-haspopup="true"
              id="exclusive-job-tooltip"
            />
          )}
        </BadgeTooltip>
      </Section>
      {jobType === "permanent" ? (
        <MultiSection>
          <MutiSectionItem>
            <SectionHeader>Salary</SectionHeader>
            <SectionContent large>
              {salaryType === "range"
                ? salaryRange
                : `${currencySymbol}${salaryMin}`}
            </SectionContent>
          </MutiSectionItem>
          {!isPortalUser && (
            <MutiSectionItem>
              <SectionHeader>
                <SectionHeaderText>Recruitment Fee</SectionHeaderText>
                {employerAccountType === "job_vendor" && (
                  <Tooltip
                    data-tip={percentageFeeTooltip}
                    data-for="recruitment-fee-tooltip"
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </Tooltip>
                )}
                <ReactTooltip
                  multiline
                  place="bottom"
                  type="dark"
                  effect="solid"
                  aria-haspopup="true"
                  id="recruitment-fee-tooltip"
                />
              </SectionHeader>

              <SectionContent large>{recruiterFeePercentage}%</SectionContent>
            </MutiSectionItem>
          )}
        </MultiSection>
      ) : (
        <MultiSection>
          <MutiSectionItem>
            <SectionHeader>Day Rate</SectionHeader>
            <SectionContent
              large
            >{`${currencySymbol}${salaryMin}`}</SectionContent>
          </MutiSectionItem>
          <MutiSectionItem>
            <SectionHeader>Contract Duration</SectionHeader>
            <SectionContent large>
              {contractDuration} {contractDurationInterval}
            </SectionContent>
          </MutiSectionItem>
        </MultiSection>
      )}
      {finalSalary && !isRecruiter && positionFilled && (
        <Section>
          <SectionHeader>Final Salary</SectionHeader>
          <SectionContent large>
            {currencySymbol}
            {finalSalary}
          </SectionContent>
        </Section>
      )}
      {keySkills && (
        <Section>
          <SectionHeader>Must Haves</SectionHeader>
          <SectionContent>
            <KeySkillsListWrapper>
              {listKeySkills.map((keySkill) => (
                <KeySkillsListItem key={keySkill}>
                  {keySkill.replace(/^\s+/g, "")}
                </KeySkillsListItem>
              ))}
            </KeySkillsListWrapper>
          </SectionContent>
        </Section>
      )}
      {agencyBriefing && <AgencyBriefing agencyBriefing={agencyBriefing} />}
      {description && <JobDescription description={description} />}
      {benefits.length > 0 ? (
        <Section>
          <SectionHeader>Benefits</SectionHeader>
          <SectionContent>
            <BenefitsListWrapper>
              {benefits.map((benefit) => (
                <BenefitsListItem key={benefit.id}>
                  {benefit.name}
                </BenefitsListItem>
              ))}
            </BenefitsListWrapper>
          </SectionContent>
        </Section>
      ) : null}
      <MultiSection>
        <MutiSectionItem>
          <SectionHeader>Job Type</SectionHeader>
          <SectionContent large capitalize>
            {jobType}
          </SectionContent>
        </MutiSectionItem>
        <MutiSectionItem>
          <SectionHeader>Degree Required</SectionHeader>
          <SectionContent large>{degreeRequired ? "Yes" : "No"}</SectionContent>
        </MutiSectionItem>
      </MultiSection>
      <MultiSection>
        <MutiSectionItem>
          <SectionHeader>Visa Sponsorship</SectionHeader>
          <SectionContent large>
            {visaSponsorship ? "Yes" : "No"}
          </SectionContent>
        </MutiSectionItem>
        <MutiSectionItem>
          <SectionHeader>Relocation</SectionHeader>
          <SectionContent large>
            {considerRelocation ? "Yes" : "No"}
          </SectionContent>
        </MutiSectionItem>
      </MultiSection>
      {jobType === "permanent" && (
        <Section>
          <SectionHeader>Reason for hire</SectionHeader>
          <SectionContent large>{vacancyReasonType}</SectionContent>
        </Section>
      )}
    </JobProfileLeftWrapper>
  );
};

JobProfileLeft.propTypes = {
  agencyBriefing: PropTypes.string,
  employerAccountType: PropTypes.string.isRequired,
  jobTitle: PropTypes.string.isRequired,
  recruiterFeePercentage: PropTypes.string.isRequired,
  salaryMin: PropTypes.number.isRequired,
  salaryRange: PropTypes.string.isRequired,
  salaryType: PropTypes.string.isRequired,
  contractDuration: PropTypes.number,
  contractDurationInterval: PropTypes.string,
  description: PropTypes.string.isRequired,
  jobType: PropTypes.string.isRequired,
  degreeRequired: PropTypes.bool.isRequired,
  visaSponsorship: PropTypes.bool.isRequired,
  considerRelocation: PropTypes.bool.isRequired,
  benefitIds: PropTypes.arrayOf(PropTypes.number),
  benefitOptions: benefitOptionsType.isRequired,
  keySkills: PropTypes.string.isRequired,
  vacancyReason: PropTypes.number.isRequired,
  vacancyReasonOptions: vacancyReasonOptionsType.isRequired,
  isRecruiter: PropTypes.bool.isRequired,
  exclusive: PropTypes.bool,
  finalSalary: PropTypes.number,
  positionFilled: PropTypes.bool.isRequired,
  publishedStatus: PropTypes.string.isRequired,
  isPortalUser: PropTypes.bool.isRequired,
  currencySymbol: PropTypes.string.isRequired,
};

JobProfileLeft.defaultProps = {
  agencyBriefing: PropTypes.isNull,
  benefitIds: PropTypes.isNull,
  contractDuration: PropTypes.isNull,
  contractDurationInterval: PropTypes.isNull,
  exclusive: PropTypes.isNull,
  finalSalary: PropTypes.isNull,
};

export default JobProfileLeft;
